import { Card, Stack, Typography, Box, Link } from '@packages/shared';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import { type FragmentType, getFragmentData } from '@packages/gql/generated/shopping';
import { LoyaltyFragmentFragmentDoc } from '@packages/gql/generated/shopping/LoyaltyFragmentFragmentDoc';
import { JoeLogo, Joe as JoePoints, Checkmark32 } from '@packages/themes/icons';
import { FormattedMessage, useIntl } from 'react-intl';

export type JoeLoyaltyProps = {
  loyaltyProgramData: FragmentType<typeof LoyaltyFragmentFragmentDoc>;
};

export const JoeLoyalty = ({ loyaltyProgramData }: JoeLoyaltyProps) => {
  const {
    order: { joe, externalUrls: { joe: joeUrl } = {} },
  } = useConfig();
  const { locale } = useIntl();
  const { basePoints, connected } = getFragmentData(LoyaltyFragmentFragmentDoc, loyaltyProgramData);

  if (!joe.enabled) {
    // Here we want to return no component if joe is not enabled for specific tenants
    return null;
  }
  if (typeof basePoints === 'undefined') {
    // Here we make the difference between undefined and null on basePoints because we want to show the component even if data is nullish
    return null;
  }
  return (
    <Stack
      sx={{
        flexDirection: 'column',
        pb: 1.5,
        width: { xs: 'calc(100% + 1.5rem)', md: '100%' },
        bgcolor: '#ffffff',
      }}
    >
      <Stack>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'center',
            bgcolor: { xs: 'grey.light', md: 'transparent' },
          }}
        >
          <JoeLogo sx={{ fontSize: '3.313rem', mb: '-0.75rem', mt: 1 }} />
        </Box>
        <Typography variant="h4" textAlign="center" m="0.875rem 0">
          <FormattedMessage defaultMessage="jö Vorteile" id="JoeLoyalty.Headline" />
        </Typography>
      </Stack>
      <Box
        sx={{
          p: { xs: '0.875rem 0.75rem 1.75rem', md: '0.938rem 1rem 1.625rem' },
          bgcolor: 'grey.light',
        }}
      >
        <Card sx={{ p: '0.75rem 0.75rem 1.313rem 0.75rem' }}>
          <Stack direction="row">
            <JoeLogo sx={{ fontSize: '2.875rem', mr: '1.375rem' }} />
            <Stack direction="column" flexGrow="1">
              <Stack
                spacing={0.5}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  justifySelf="flex-start"
                  fontWeight="fontWeightSemiBold"
                  variant="body1"
                  component="span"
                >
                  <FormattedMessage
                    id="JoeLoyalty.Points.Message"
                    defaultMessage="Werden gesammelt: "
                  />
                </Typography>
                <Typography
                  component="span"
                  variant="body1"
                  sx={{ fontWeight: 'fontWeightSemiBold', display: 'flex', alignItems: 'center' }}
                >
                  <FormattedMessage
                    defaultMessage="{joePoints}"
                    values={{ joePoints: basePoints ?? '--' }} // Here we use nullish coalescense operator to show -- if basePoints are null so atleast something is displayed
                    id="JoeLoyalty.Points.Value"
                  />

                  <JoePoints sx={{ fontSize: '1rem', ml: 0.5 }} />
                </Typography>
              </Stack>

              <Typography component="span" variant="body3" color="text.darkTransparent">
                {connected && (
                  <FormattedMessage
                    id="JoeLoyalty.Verified.Message"
                    defaultMessage="Ihre persönlichen Coupons können auf Schritt 5 eingegeben werden."
                  />
                )}
              </Typography>
            </Stack>
          </Stack>
          {!connected && (
            <>
              <Typography
                mt={2}
                mb={0.5}
                variant="body2"
                fontWeight="fontWeightSemiBold"
                component="div"
              >
                <FormattedMessage
                  id="JoeLoyalty.Unverified.Headline"
                  defaultMessage="Sind Sie bereits Kunde bei jö?"
                />
              </Typography>
              <Typography mb={1.5} variant="body2" component="div">
                <FormattedMessage
                  id="JoeLoyalty.Unverified.Message"
                  defaultMessage="Auf Schritt 5 können Sie Ihr Konto ganz einfach verknüpfen, Ös sammeln und viel Geld sparen."
                />
              </Typography>
              <Box border="solid 1px" borderColor="grey.main" borderRadius="3px" p="1.063rem">
                <Stack>
                  <Typography
                    textAlign="center"
                    fontWeight="fontWeightSemiBold"
                    variant="body2"
                    component="p"
                  >
                    <FormattedMessage
                      id="JoeLoyalty.Advantages.Headline"
                      defaultMessage="jö Vorteile im Überblick"
                    />
                  </Typography>
                  <Box component="ul" pl="0" mt="0.563rem">
                    <Stack
                      flexDirection="row"
                      justifyContent="flex-start"
                      gap={1.5}
                      component="li"
                      mb="0.438rem"
                    >
                      <Checkmark32 sx={{ fill: '#d6ab53', fontSize: '23px' }} />
                      <Typography textAlign="left" variant="body2" component="div">
                        <FormattedMessage
                          id="JoeLoyalty.Advantages.First"
                          defaultMessage="Bei jedem Einkauf Ös sammeln:{break}
                      <strong>Pro 2€ erhalten Sie ein Ö</strong>"
                          values={{
                            break: <br />,
                            strong: (chunks) => (
                              <Typography fontWeight="fontWeightSemiBold" variant="body2">
                                {chunks}
                              </Typography>
                            ),
                          }}
                        />
                      </Typography>
                    </Stack>
                    <Stack
                      sx={{ flexDirection: 'row', justifyContent: 'flex-start', gap: 1.5 }}
                      component="li"
                    >
                      <Checkmark32 sx={{ fill: '#d6ab53', fontSize: '23px' }} />
                      <Typography textAlign="left" variant="body2" component="span">
                        <FormattedMessage
                          id="JoeLoyalty.Advantages.Second"
                          defaultMessage="Durch die Eingabe von Aktionscodes mehrfach Ös erhalten."
                        />
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
              {joeUrl && (
                <Typography
                  mt={2}
                  variant="body2"
                  component="div"
                  alignItems="center"
                  display="flex"
                >
                  <JoePoints sx={{ fontSize: '1rem', mr: 0.5 }} />
                  <FormattedMessage
                    id="JoeLoyalty.Unverified.Register"
                    defaultMessage="Noch kein jö Mitgleid? <joeLink>Jetzt registrieren</joeLink>"
                    values={{
                      joeLink: (chunks: React.ReactNode) => (
                        <Link
                          ml={0.5}
                          href={getLocalizedPath(joeUrl, locale)}
                          target="_blank"
                          color="text.dark"
                        >
                          {chunks}
                        </Link>
                      ),
                    }}
                  />
                </Typography>
              )}
            </>
          )}
        </Card>
      </Box>
    </Stack>
  );
};
